import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
} from 'lucide-react';
import { toast } from '../../../../ui/toast';
import { CustomSelect } from '../../../../ui/custom_react_select/custom_select';
import { TextInput } from '../../../../ui/input/text_input/text_input';
import {
  deletePreScreenQuestion,
  editPreScreenQuestion,
} from '../../../../utils/api_service/pre_screen_api';
import {
  PreScreenQuestionBank,
  PreScreenQuestionType,
} from '../../../../utils/types/assessment_types';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import { questionTypeOptions } from '../PreScreenSearch';

export interface PreScreenQuestionProps {
  question: PreScreenQuestionBank;
  deleteQuestion: (questionId: string) => void;
  editQuestion: (editQuestion: PreScreenQuestionBank) => void;
  validateQuestion: (
    questionText: string,
    questionType: PreScreenQuestionType,
    choices: string[],
    defaultQuestion: boolean,
  ) => PreScreenQuestionBank | null;
}

export const PreScreenQuestion = ({
  question,
  deleteQuestion,
  editQuestion,
  validateQuestion,
}: PreScreenQuestionProps) => {
  const [edit, setEdit] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editQuestionType, setEditQuestionType] =
    useState<PreScreenQuestionType>(question.questionType);
  const [editQuestionText, setEditQuestionText] = useState<string>(
    question.question,
  );
  const [editChoices, setEditChoices] = useState<string[]>(
    question.choices && question.choices.length !== 0 ? question.choices : [''],
  );
  const [editDefaultQuestion, setEditDefaultQuestion] = useState<boolean>(
    question.defaultQuestion,
  );

  const handleChoiceChange = (index: number, value: string) => {
    const newChoices = [...editChoices];
    newChoices[index] = value;
    setEditChoices(newChoices);
  };

  const handleDeleteQuestion = async () => {
    try {
      if (question.id) {
        const response = await deletePreScreenQuestion(question.id);
        if (response.ok) {
          deleteQuestion(question.id);
        } else {
          toast.error('Error deleting question', { position: 'bottom-center' });
        }
      }
    } catch (error) {
      toast.error('Error deleting question', { position: 'bottom-center' });
    }
  };

  const handleSaveQuestion = async () => {
    try {
      if (question.id) {
        let newQuestionData = validateQuestion(
          editQuestionText,
          editQuestionType,
          editChoices,
          editDefaultQuestion,
        );

        if (!newQuestionData) return;
        newQuestionData = { ...newQuestionData, id: question.id };
        const response = await editPreScreenQuestion(newQuestionData);
        if (response.ok) {
          const result = await response.json();
          editQuestion(result);
          setEdit(false);
          setIsOpen(false);
        } else {
          toast.error('Error saving question', { position: 'bottom-center' });
        }
      }
    } catch (error) {
      toast.error('Error saving question', { position: 'bottom-center' });
    }
  };

  const handleEditQuestion = () => {
    setEdit(true);
    setIsOpen(true);
  };

  const questionTypeLabel =
    questionTypeOptions.find((option) => option.value === question.questionType)
      ?.label || '';

  const getBorderColor = () => {
    if (edit) return '#6ee7b7';
    if (question.defaultQuestion) return '#fcd34d';
    return '#e2e8f0';
  };

  return (
    <Paper
      variant='outlined'
      sx={{
        mb: 1.5,
        width: '100%',
        borderRadius: 2,
        borderColor: getBorderColor(),
        bgcolor: '#ffffff',
        transition: 'border-color 0.15s ease',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.75,
          py: 1.25,
          cursor: 'pointer',
        }}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((prev) => !prev);
          }}
          aria-label={isOpen ? 'Collapse question' : 'Expand question'}
          sx={{ color: 'text.secondary', p: 0.5 }}
        >
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant='body2'
            fontWeight={600}
            color='text.primary'
            noWrap
          >
            {question.question}
          </Typography>
          <Stack direction='row' spacing={1} sx={{ mt: 0.5 }} alignItems='center'>
            <Chip
              label={questionTypeLabel}
              size='small'
              sx={{
                bgcolor: 'grey.100',
                color: 'text.secondary',
                fontSize: '0.6875rem',
                fontWeight: 600,
                height: 20,
              }}
            />
            {question.defaultQuestion && (
              <Chip
                label='Default'
                size='small'
                sx={{
                  bgcolor: '#fef3c7',
                  color: '#b45309',
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  height: 20,
                }}
              />
            )}
          </Stack>
        </Box>

        <Stack direction='row' spacing={0.5} alignItems='center'>
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              handleEditQuestion();
            }}
            aria-label='Edit question'
            sx={{
              color: '#0d9488',
              '&:hover': { bgcolor: 'rgba(13, 148, 136, 0.08)' },
            }}
          >
            <Pencil size={16} />
          </IconButton>
          <IconButton
            size='small'
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteQuestion();
            }}
            aria-label='Delete question'
            sx={{
              color: '#dc2626',
              '&:hover': { bgcolor: 'rgba(220, 38, 38, 0.08)' },
            }}
          >
            <Trash2 size={16} />
          </IconButton>
        </Stack>
      </Box>

      {isOpen && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 2,
            py: 1.5,
          }}
        >
          {edit ? (
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                  gap: 1.5,
                }}
              >
                <Box>
                  <TextInput
                    label='Question'
                    name='question'
                    value={editQuestionText}
                    type='text'
                    onChange={(e) => setEditQuestionText(e.target.value)}
                  />
                </Box>
                <Box>
                  <CustomSelect
                    label='Question Type'
                    name='questionType'
                    value={questionTypeOptions.find(
                      (option) => option.value === editQuestionType,
                    )}
                    options={questionTypeOptions}
                    onChange={(selectedOption: SelectOptions) =>
                      setEditQuestionType(
                        selectedOption.value as PreScreenQuestionType,
                      )
                    }
                  />
                </Box>
              </Box>

              {editQuestionType === 'multiple-choice' && (
                <Stack spacing={1}>
                  <Typography
                    variant='caption'
                    fontWeight={700}
                    color='text.secondary'
                    textTransform='uppercase'
                  >
                    Options:
                  </Typography>
                  {editChoices.map((choice, index) => (
                    <Stack
                      key={index}
                      direction='row'
                      spacing={1}
                      alignItems='center'
                    >
                      <Box sx={{ flex: 1 }}>
                        <TextInput
                          name='choice'
                          value={choice}
                          type='text'
                          onChange={(e) =>
                            handleChoiceChange(index, e.target.value)
                          }
                        />
                      </Box>
                      {index === editChoices.length - 1 && (
                        <Button
                          variant='outlined'
                          size='small'
                          onClick={() => setEditChoices([...editChoices, ''])}
                          sx={{
                            borderColor: '#cbd5e1',
                            color: '#334155',
                            textTransform: 'none',
                            fontWeight: 600,
                            height: 38,
                            borderRadius: 1.5,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Add Choice
                        </Button>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pt: 1,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size='small'
                      checked={editDefaultQuestion}
                      onChange={() =>
                        setEditDefaultQuestion(!editDefaultQuestion)
                      }
                      sx={{
                        color: '#10b981',
                        '&.Mui-checked': { color: '#10b981' },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant='caption'
                      fontWeight={500}
                      color='text.secondary'
                    >
                      Set as default
                    </Typography>
                  }
                />
                <Button
                  variant='contained'
                  onClick={handleSaveQuestion}
                  sx={{
                    bgcolor: '#10b981',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#059669',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Save
                </Button>
              </Box>
            </Stack>
          ) : (
            question.questionType === 'multiple-choice' && (
              <Stack spacing={1}>
                <Typography
                  variant='caption'
                  fontWeight={700}
                  color='text.secondary'
                >
                  Options:
                </Typography>
                <Stack direction='row' spacing={1} flexWrap='wrap'>
                  {question.choices?.map((choice, idx) => (
                    <Chip
                      key={idx}
                      label={choice}
                      size='small'
                      sx={{
                        bgcolor: 'grey.100',
                        color: 'text.primary',
                        fontSize: '0.75rem',
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            )
          )}
        </Box>
      )}
    </Paper>
  );
};
